import React, { createContext, useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { clearAllAuthData } from '../utils/authUtils';
import { AUTH_ENDPOINTS, CLIENTE_ENDPOINTS, API_BASE_URL } from '../utils/apiConfig';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Initialize auth state from localStorage and validate with server
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      const storedUserRole = localStorage.getItem('userRole');
      const storedUserData = JSON.parse(localStorage.getItem('userData') || 'null');
      
      if (token && storedUserRole) {
        try {
          // API endpoint to validate token based on user role
          const endpoint = storedUserRole === 'user' 
            ? AUTH_ENDPOINTS.ME
            : CLIENTE_ENDPOINTS.ME;
          
          const response = await fetch(endpoint, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            // Token is valid, set auth state
            setIsAuthenticated(true);
            setUserRole(storedUserRole);
            setUserData(storedUserData);
          } else {
            // Token is invalid or expired, clear localStorage
            localStorage.removeItem('token');
            localStorage.removeItem('userRole');
            localStorage.removeItem('isAuthenticated');
            localStorage.removeItem('userData');
            
            setIsAuthenticated(false);
            setUserRole(null);
            setUserData(null);
          }
        } catch (error) {
          console.error('Token validation error:', error);
          setIsAuthenticated(false);
          setUserRole(null);
          setUserData(null);
        }
      } else {
        // No token found, user is not authenticated
        setIsAuthenticated(false);
        setUserRole(null);
        setUserData(null);
      }
      
      setLoading(false);
    };
    
    checkAuth();
  }, []);

  // Login function
  const login = (token, role, user) => {    
    // Clean up any existing authentication data
    clearAllAuthData();
    
    // Store the new authentication data
    localStorage.setItem('token', token);
    localStorage.setItem('userRole', role);
    localStorage.setItem('isAuthenticated', 'true');
    localStorage.setItem('userData', JSON.stringify(user));
    
    // Explicitly store the user type if it's a system user
    if (role === 'user' && user.permissions) {
      // Determine user type based on permissions
      let userType = 'associado'; // Default
      if (user.permissions.includes('Master')) {
        userType = 'master';
      } else if (user.permissions.includes('Diretoria')) {
        userType = 'diretoria';
      } else if (user.permissions.includes('Gestor')) {
        userType = 'gestor';
      }
      
      localStorage.setItem('userType', userType);
    }
    
    setIsAuthenticated(true);
    setUserRole(role);
    setUserData(user);
    
    // Get current path to avoid unnecessary navigation
    const currentPath = window.location.pathname;
    
    if (role === 'user' && !currentPath.startsWith('/painel')) {
      navigate('/painel');
    } else if (role === 'cliente' && !currentPath.startsWith('/cliente')) {
      navigate('/cliente');
    }
  };

  // Logout function
  const logout = () => {   
    // Clear all authentication data
    clearAllAuthData();
    
    // Reset state
    
    setIsAuthenticated(false);
    setUserRole(null);
    setUserData(null);
    
    navigate('/login');
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      userRole,
      userData,
      loading,
      login,
      logout
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

// Protected Route Component
export const ProtectedRoute = ({ element, allowedRoles = [] }) => {
  const { isAuthenticated, userRole, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading) {
      if (!isAuthenticated) {
        navigate('/login');
      } else if (allowedRoles.length > 0 && !allowedRoles.includes(userRole)) {
        if (userRole === 'user') {
          navigate('/painel');
        } else if (userRole === 'cliente') {
          navigate('/cliente');
        } else {
          navigate('/login');
        }
      }
    }
  }, [isAuthenticated, userRole, loading, navigate, allowedRoles]);

  if (loading) {
    return <div>Carregando...</div>;
  }

  return isAuthenticated && (allowedRoles.length === 0 || allowedRoles.includes(userRole)) 
    ? element 
    : null;
};